import { useState, useEffect } from 'react';
import ProductBrand from '@/components/feature/ProductBrand';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validating, setValidating] = useState(true);

  useEffect(() => {
    const validateRecovery = async () => {
      // Try hash fragment first (Supabase default), then query params
      const hash = window.location.hash;
      const query = window.location.search;
      
      console.log('Reset page loaded. Hash:', hash, 'Query:', query);

      let accessToken: string | null = null;
      let refreshToken: string | null = null;
      let type: string | null = null;

      if (hash) {
        const params = new URLSearchParams(hash.substring(1));
        accessToken = params.get('access_token');
        refreshToken = params.get('refresh_token');
        type = params.get('type');
      }

      // Fallback: also check query params
      if (!accessToken && query) {
        const params = new URLSearchParams(query);
        accessToken = params.get('access_token');
        refreshToken = params.get('refresh_token');
        type = params.get('type');
      }

      if (type !== 'recovery' || !accessToken) {
        setError('Link không hợp lệ hoặc đã hết hạn.');
        setValidating(false);
        return;
      }

      const { error: err } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken || '',
      });

      if (err) {
        console.error('Session set error:', err);
        setError('Link đã hết hạn. Vui lòng yêu cầu link mới.');
      }

      setValidating(false);
    };

    validateRecovery();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!password || !confirmPassword) {
      setError('Vui lòng nhập đầy đủ thông tin!');
      return;
    }
    if (password.length < 6) {
      setError('Mật khẩu phải có ít nhất 6 ký tự!');
      return;
    }
    if (password !== confirmPassword) {
      setError('Mật khẩu xác nhận không khớp!');
      return;
    }

    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (err) {
      console.error('Update password error:', err);
      setError(err.message);
    } else {
      setSuccess(true);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-[#f8f7f4] flex items-center justify-center p-4">
        <div className="text-center">
          <ProductBrand compact />
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <i className="ri-loader-4-line animate-spin text-lg"></i>
            <span>Đang xác thực...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f7f4] flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-amber-100/60 rounded-full"></div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-amber-50/80 rounded-full"></div>
      </div>

      <div className="relative w-full max-w-md">
        <ProductBrand />

        {/* Card */}
        <div className="bg-white rounded-2xl p-8">
          {success ? (
            <div className="text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                <span className="w-6 h-6 flex items-center justify-center text-emerald-500">
                  <i className="ri-checkbox-circle-line text-xl"></i>
                </span>
              </div>
              <h2 className="text-lg font-bold text-[#1a2332]">Đặt lại mật khẩu thành công!</h2>
              <p className="text-gray-400 text-xs">
                Mật khẩu của bạn đã được cập nhật. Hãy đăng nhập lại bằng mật khẩu mới.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3 bg-[#f59e0b] text-white rounded-xl text-sm font-bold hover:bg-[#d97706] transition-colors cursor-pointer whitespace-nowrap"
              >
                Đăng nhập ngay
              </button>
            </div>
          ) : error ? (
            <div className="text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
                <span className="w-6 h-6 flex items-center justify-center text-red-500">
                  <i className="ri-error-warning-line text-xl"></i>
                </span>
              </div>
              <h2 className="text-lg font-bold text-[#1a2332]">Link không hợp lệ</h2>
              <p className="text-red-500 text-xs">{error}</p>
              <button
                onClick={() => navigate('/forgot-password')}
                className="w-full py-3 bg-[#f59e0b] text-white rounded-xl text-sm font-bold hover:bg-[#d97706] transition-colors cursor-pointer whitespace-nowrap"
              >
                Yêu cầu link mới
              </button>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
                  <span className="w-6 h-6 flex items-center justify-center text-amber-500">
                    <i className="ri-lock-unlock-line text-xl"></i>
                  </span>
                </div>
                <h2 className="text-lg font-bold text-[#1a2332]">Đặt lại mật khẩu</h2>
                <p className="text-gray-400 text-xs mt-1">
                  Tạo mật khẩu mới cho tài khoản của bạn.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* New Password */}
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1.5">Mật khẩu mới</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-lock-line text-base"></i>
                    </span>
                    <input
                      type={showPass ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Tối thiểu 6 ký tự"
                      className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#f59e0b] transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className={showPass ? 'ri-eye-off-line text-base' : 'ri-eye-line text-base'}></i>
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1.5">Xác nhận mật khẩu</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-lock-2-line text-base"></i>
                    </span>
                    <input
                      type={showPass ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="Nhập lại mật khẩu"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#f59e0b] transition-colors"
                    />
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
                    <span className="w-4 h-4 flex items-center justify-center text-red-500 shrink-0">
                      <i className="ri-error-warning-line text-sm"></i>
                    </span>
                    <p className="text-red-600 text-xs">{error}</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-[#f59e0b] text-white rounded-xl text-sm font-bold hover:bg-[#d97706] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-base"></i>
                      Đang cập nhật...
                    </span>
                  ) : (
                    'Đặt lại mật khẩu'
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        <ProductBrand footer />
      </div>
    </div>
  );
}